<template>
    <v-dialog v-model="dialog" max-width="500px">
      <template v-slot:activator="{ on }">
        <v-btn color="primary" outlined class="hidden-md-and-down header-list-button" v-on="on">{{
          $t("pages.refund.dialog.create")
        }}</v-btn>
        <v-btn color="primary" outlined class="header-list-button hidden-lg-and-up" v-on="on"
          ><v-icon>$icons_system_plus</v-icon></v-btn
        >
      </template>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("pages.refund.dialog.title") }}</span>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-container class="pa-0">
            <v-row no-gutters>
              <v-container class="white">
                <v-row no-gutters>
                  <v-col cols="10" offset="1">
                    <p class="font-weight-bold black--text">
                      {{ $t("pages.refund.dialog.dispatchId") }}
                    </p>
                    <v-text-field
                      v-model="dispatchId"
                      pattern="[0-9]*"
                      append-icon="$icons_system_search"
                      :label="$t('pages.refund.dialog.dispatchId')"
                      single-line
                      clearable
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>

              <v-container class="grey lighten-3" v-if="charge !== null">
                <v-row no-gutters>
                  <v-col cols="10" offset="1">
                    <p class="font-weight-bold black--text">
                      {{ $t("pages.refund.dialog.bookingDetails") }}
                    </p>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="4" offset="1">
                    <small class="font-weight-bold">{{
                      $t("pages.refund.dialog.transactionDate")
                    }}</small>
                    <v-text-field
                      background-color="white"
                      v-model="transactionDate"
                      dense
                      outlined
                      readonly
                      prepend-inner-icon="$icons_product_calendar"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" offset="2">
                    <small class="font-weight-bold">{{
                      $t("pages.refund.dialog.transactionTime")
                    }}</small>
                    <v-text-field
                      background-color="white"
                      v-model="transactionTime"
                      dense
                      outlined
                      readonly
                      prepend-inner-icon="$icons_product_calendar"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="4" offset="1">
                    <small class="font-weight-bold">{{
                      $t("pages.refund.dialog.cardIssuer")
                    }}</small>
                    <v-text-field
                      background-color="white"
                      v-model="cardBrand"
                      dense
                      outlined
                      readonly
                      prepend-inner-icon="$icons_product_credit_card"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" offset="2">
                    <small class="font-weight-bold">{{
                      $t("pages.refund.dialog.fareAmount")
                    }}</small>
                    <v-text-field
                      background-color="white"
                      v-model="fareAmount"
                      dense
                      outlined
                      readonly
                      prepend-inner-icon="$icons_product_banking_bill"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>

              <v-container justify-center pa-10 class="white" v-if="loadingCharge">
                <v-row no-gutters>
                  <v-col cols="12" class="text-center">
                    <v-progress-circular indeterminate></v-progress-circular>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" class="text-center">
                    <p class="font-weight-bold">{{ $t("pages.refund.dialog.loadingCharge") }}</p>
                  </v-col>
                </v-row>
              </v-container>

              <v-container class="white" v-if="charge !== null">
                <template v-if="!isChargeOnDeprecatedPaymentConnector">
                  <v-alert type="warning">
                    This transaction is available on the Javelin platform, please proceed to refund the transaction in Javelin.
                  </v-alert>
                </template>
                <template v-else>
                  <v-row no-gutters>
                    <v-col cols="4" offset="1">
                      <p class="font-weight-bold">{{ $t("pages.refund.dialog.refundAmount") }}</p>
                      <v-text-field
                        v-model.number="refundAmount"
                        outlined
                        dense
                        prepend-inner-icon="$icons_product_banking_bill"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4" offset="2">
                      <p class="font-weight-bold">{{ $t("pages.refund.dialog.remainingAmount") }}</p>
                      <v-text-field
                        v-model="remainingAmount"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="$icons_product_banking_bill"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
              </v-container>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions v-if="charge !== null && isChargeOnDeprecatedPaymentConnector">
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="dialog = false">{{
            $t("pages.refund.dialog.cancel")
          }}</v-btn>
          <v-btn color="primary" @click="createChargeRefund()" :loading="refundState==='creating'" :disabled="!submitRefundPossible">{{
            $t("pages.refund.dialog.submit")
          }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>
<script>
import * as _ from 'lodash';
import debounce from 'debounce';
import Decimal from 'decimal.js-light';
import dayjs from '../plugins/dayjs';

export default {
  props: {
    charge: {
      type: Object,
      required: false,
    },
    loadingCharge: {
      type: Boolean,
      required: true,
    },
    refundState: {
      type: String,
      required: false,
    },
  },
  data: () => ({
    dialog: false,
    dispatchId: '',
    refundAmount: null,
  }),

  created() {
    this.searchByDispatchIdDebounced = debounce(this.searchByDispatchId, 500);
  },

  watch: {
    dispatchId() {
      if (!this.dispatchId) {
        this.charge = null;
      } else {
        this.searchByDispatchIdDebounced();
      }
    },
    charge() {
      this.refundAmount = this.fareAmount;
    },
    refundState(newStatus) {
      if (newStatus === 'created') {
        this.dialog = false;
      }
    },
  },

  computed: {
    isChargeOnDeprecatedPaymentConnector() {
      const providerName = _.get(this.charge, 'provider.instance');
      return this.charge && providerName && providerName !== 'uk';
    },
    remainingAmount() {
      if (!this.refundAmount || !this.fareAmount) return this.fareAmount;
      return Decimal(this.fareAmount).minus(this.refundAmount).toFixed(2);
    },
    fareAmount() {
      return this.charge && this.charge.booking ? this.charge.booking.amount : null;
    },
    cardBrand() {
      return this.charge && this.charge.card ? this.charge.card.brand : null;
    },
    transactionTime() {
      return this.charge ? this.formatTime(this.charge.captureDate) : null;
    },
    transactionDate() {
      return this.charge ? this.formatDate(this.charge.captureDate) : null;
    },
    submitRefundPossible() {
      return this.charge && this.refundAmount && this.refundAmount <= this.fareAmount;
    },
  },

  methods: {
    async searchByDispatchId() {
      if (this.dispatchId.length > 3) {
        this.$emit('searchByDispatchId', {
          dispatchId: this.dispatchId,
        });
      }
    },

    async createChargeRefund() {
      await this.$emit('createChargeRefund', {
        chargeId: this.charge.id,
        refundAmount: this.refundAmount,
      });
    },

    formatDate(date) {
      if (!date) return null;
      return dayjs(date).format('YYYY-MM-DD');
    },

    formatTime(date) {
      if (!date) return null;
      return dayjs(date).format('HH:mm:ss');
    },
  },
};
</script>
